import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Divider, IconButton, LinearProgress, Typography,
} from '@mui/material';
import {
  Add as AddIcon, ArrowBack, CheckCircle, CreditCard, Remove as RemoveIcon,
} from '@mui/icons-material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import FlatRemovalTree, { FlatTreeProperty } from './FlatRemovalTree';


const StripeCheckoutForm: React.FC<{ label: string; onPaid: () => Promise<void> }> = ({ label, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !ready) return;
    setProcessing(true);
    setError(null);
    const { error: submitError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/dashboard` },
      redirect: 'if_required',
    });
    if (submitError) {
      setError(submitError.message || 'Payment failed');
      setProcessing(false);
      return;
    }
    try {
      await onPaid();
    } catch (err: any) {
      setError(err.message || 'Your payment went through but we could not finish setting up your plan. Please contact support.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement onReady={() => setReady(true)} />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Button type="submit" variant="contained" fullWidth size="large" disabled={!stripe || processing || !ready} sx={{ mt: 3 }}>
        {processing || !ready ? <CircularProgress size={24} color="inherit" /> : label}
      </Button>
    </form>
  );
};

// Per-flat plan card: starts at the flats already in the portfolio; fewer flats means choosing which to delete.
const FlatPlanForm: React.FC<{ stripePromise: Promise<Stripe | null> | null; onSuccess: () => void }> = ({ stripePromise, onSuccess }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [unitAmount, setUnitAmount] = useState<number | null>(null);
  const [currency, setCurrency] = useState('gbp');
  const [properties, setProperties] = useState<FlatTreeProperty[]>([]);
  const [flatCount, setFlatCount] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [step, setStep] = useState<'select' | 'pay'>('select');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${config.apiUrl}/api/payments/flat-plan`, { headers }),
      axios.get(`${config.apiUrl}/api/flats`, { headers }),
    ]).then(([plan, flats]) => {
      setUnitAmount(plan.data.unit_amount);
      setCurrency(plan.data.currency);
      setFlatCount(plan.data.flat_count);
      setQuantity(Math.max(1, plan.data.flat_count));
      setProperties(flats.data.properties || []);
    }).catch(err => setError(err.response?.data?.error || 'Could not load the plan'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const money = (pence: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(pence / 100);

  const mustRemove = Math.max(0, flatCount - quantity);
  const stillToRemove = mustRemove - removed.size;
  const total = unitAmount !== null ? money(unitAmount * quantity) : '';

  const changeQuantity = (next: number) => {
    setQuantity(Math.max(1, next));
    setRemoved(new Set());
  };

  const toggleRemoved = (key: string) => {
    setRemoved(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else if (next.size < mustRemove) next.add(key);
      return next;
    });
  };

  const handleContinue = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await axios.post(`${config.apiUrl}/api/payments/create-flat-subscription`, { flats: quantity }, { headers });
      setClientSecret(res.data.client_secret);
      setStep('pay');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set up payment');
    } finally {
      setCreating(false);
    }
  };

  // The card payment can take a moment to show up as paid on Stripe's side, so retry briefly.
  const confirmPurchase = async () => {
    const remove_units = Array.from(removed).map(key => {
      const [normalized_address, flat] = key.split('||');
      return { normalized_address, flat };
    });
    for (let attempt = 0; attempt < 6; attempt++) {
      try {
        await axios.post(`${config.apiUrl}/api/payments/confirm-flat-subscription`, { remove_units }, { headers });
        onSuccess();
        return;
      } catch (err: any) {
        if (err.response?.status === 409 && err.response.data?.retry) {
          await new Promise(r => setTimeout(r, 1000));
          continue;
        }
        throw new Error(err.response?.data?.error || 'Could not finish setting up your plan');
      }
    }
    throw new Error('Your payment is still being confirmed. Refresh in a minute; if your plan is not active, contact support.');
  };

  return (
    <>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <CreditCard sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5" component="h2" fontWeight={700}>Choose your plan</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          You'll be charged immediately. Cancel anytime from your profile.
        </Typography>
      </Box>

      {['Full access to all features', 'AI certificate extraction', 'Expiry reminders & Gmail integration'].map(perk => (
        <Box key={perk} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
          <Typography variant="body2">{perk}</Typography>
        </Box>
      ))}

      <Divider sx={{ my: 3 }} />

      {loading ? (
        <LinearProgress sx={{ borderRadius: 1 }} />
      ) : step === 'select' ? (
        <>
          <Typography variant="subtitle2" fontWeight={600}>Compliance plan</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {unitAmount !== null && `${money(unitAmount)} / flat / month. `}
            {flatCount > 0
              ? `Your portfolio has ${flatCount} flat${flatCount === 1 ? '' : 's'}, so we started with that. Change it if you want more or fewer.`
              : 'Choose how many flats you want to manage.'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1 }}>
            <IconButton aria-label="Fewer flats" onClick={() => changeQuantity(quantity - 1)} disabled={quantity <= 1} sx={{ border: 1, borderColor: 'divider' }}>
              <RemoveIcon />
            </IconButton>
            <Box sx={{ textAlign: 'center', minWidth: 90 }}>
              <Typography variant="h4" fontWeight={700} component="div">{quantity}</Typography>
              <Typography variant="body2" color="text.secondary">flat{quantity === 1 ? '' : 's'}</Typography>
            </Box>
            <IconButton aria-label="More flats" onClick={() => changeQuantity(quantity + 1)} sx={{ border: 1, borderColor: 'divider' }}>
              <AddIcon />
            </IconButton>
          </Box>
          <Typography textAlign="center" fontWeight={600} sx={{ mb: 2 }}>
            {unitAmount !== null && `${total} / month`}
          </Typography>

          {mustRemove > 0 && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                You have {flatCount} flats but chose {quantity}. Pick the {mustRemove} flat{mustRemove === 1 ? '' : 's'} to delete.
                Their certificates will be permanently deleted once you pay.
                {stillToRemove > 0 ? ` ${stillToRemove} more to choose.` : ''}
              </Alert>
              <FlatRemovalTree properties={properties} removed={removed} mustRemove={mustRemove} onToggle={toggleRemoved} />
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Button variant="contained" fullWidth size="large" onClick={handleContinue} disabled={creating || stillToRemove > 0 || unitAmount === null}>
            {creating ? <CircularProgress size={24} color="inherit" /> : `Continue — ${total} / month`}
          </Button>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {quantity} flat{quantity === 1 ? '' : 's'} — {total} / month
            </Typography>
            <Button size="small" startIcon={<ArrowBack />} onClick={() => { setStep('select'); setClientSecret(null); setError(null); }}>
              Change
            </Button>
          </Box>
          {clientSecret && stripePromise ? (
            <Elements stripe={stripePromise} options={{ clientSecret }} key={clientSecret}>
              <StripeCheckoutForm label={`Subscribe — ${total} / month`} onPaid={confirmPurchase} />
            </Elements>
          ) : null}
        </>
      )}
    </>
  );
};

export default FlatPlanForm;
